import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  AlertTriangle,
  CheckCircle2,
  Download,
  RefreshCw,
  TrendingDown,
  Landmark,
  Gavel,
  Zap,
  ShieldCheck,
  MessageSquare,
} from "lucide-react";
import { DatePicker, Modal, Input, InputNumber, Select, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import LoanWhatsAppModal from "../components/LoanWhatsAppModal";
import { fintechApi } from "../services/api";

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

const formatDate = (val) => {
  if (!val) return "-";
  const d = dayjs(val);
  return d.isValid() ? d.format("DD/MM/YYYY") : String(val);
};

const EMI_STATUS_STYLES = {
  PAID: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PENDING: "bg-gray-100 text-gray-700 border-gray-200",
  OVERDUE: "bg-red-50 text-red-700 border-red-200 animate-pulse",
  PARTIAL: "bg-amber-50 text-amber-700 border-amber-200",
};

const ACCOUNT_STATUS_STYLES = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  OVERDUE: "bg-red-50 text-red-700 border-red-200",
  NPA: "bg-red-100 text-red-800 border-red-300",
  CLOSED: "bg-slate-100 text-slate-700 border-slate-300",
};

// NACH mandate ke badge styles — NOT_SETUP humara local default hai, backend nahi bhejta
const NACH_STATUS_STYLES = {
  NOT_SETUP: "bg-gray-100 text-gray-600 border-gray-200",
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  FAILED: "bg-red-50 text-red-700 border-red-200",
  CANCELLED: "bg-slate-100 text-slate-700 border-slate-300",
  REJECTED: "bg-red-50 text-red-700 border-red-200",
};

const NACH_STATUS_LABELS = {
  NOT_SETUP: "Not Setup",
  PENDING: "Pending Bank Approval",
  ACTIVE: "Active",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
  REJECTED: "Rejected",
};

const EMPTY_PAYMENT_FORM = { amount: 0, mode: "UPI", transactionId: "", remarks: "" };

const EMPTY_NACH_FORM = {
  bankAccountNumber: "",
  ifscCode: "",
  accountHolderName: "",
  maxDebitAmount: null,
  startDate: null,
};

const EMPTY_DEBIT_FORM = { amount: null, remarks: "" };

// Standard RBI IFSC — 4 letters + 0 + 6 alphanumeric (total 11 chars)
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

// Legal notice in classifications pe hi allowed hai
const LEGAL_NOTICE_CLASSIFICATIONS = ["SMA_2", "SMA2", "SMA-2", "NPA"];

// 404 ya "not found" ka matlab record hai hi nahi — ye error nahi hai
const isNotFoundError = (err) =>
  err?.status === 404 ||
  err?.response?.status === 404 ||
  /(^|\D)404(\D|$)|not\s*found/i.test(err?.message || "");

// File name me slash / hash na jaaye warna browser download tod deta hai
const safeFileName = (val) => String(val || "account").replace(/[^a-zA-Z0-9._-]+/g, "_");

// Blob ko file bana ke download karao — repo ka standard pattern (Leads/OrdersList wagairah)
const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Mandate response ke field names backend version ke hisaab se alag hote hain
const normalizeMandate = (res) => {
  const raw = res?.data && typeof res.data === "object" ? res.data : res;
  if (!raw || typeof raw !== "object" || Array.isArray(raw) || Object.keys(raw).length === 0) return null;
  const status = String(raw.status || raw.mandateStatus || "").toUpperCase().replace(/[\s-]+/g, "_");
  return {
    ...raw,
    mandateId: raw.mandateId || raw.umrn || raw.mandateReference || raw.id || "",
    status: status || "NOT_SETUP",
    bankAccountNumber: raw.bankAccountNumber || raw.accountNumber || "",
    ifscCode: raw.ifscCode || raw.ifsc || "",
    accountHolderName: raw.accountHolderName || raw.holderName || "",
    maxDebitAmount: Number(raw.maxDebitAmount ?? raw.maxAmount) || 0,
    startDate: raw.startDate || raw.mandateStartDate || null,
    lastDebitDate: raw.lastDebitDate || raw.lastDebitedOn || null,
  };
};

// Account number mask — sirf last 4 digits dikhao
const maskAccountNumber = (val) => {
  const str = String(val || "").trim();
  if (!str) return "-";
  return str.length <= 4 ? str : `${"X".repeat(str.length - 4)}${str.slice(-4)}`;
};

const toArray = (val) =>
  Array.isArray(val) ? val : Array.isArray(val?.content) ? val.content : Array.isArray(val?.data) ? val.data : [];

const normalizeAccount = (a, fallbackId) => ({
  ...a,
  id: a.id ?? fallbackId,
  accountNumber: a.accountNumber || a.loanAccountNumber || `#${a.id ?? fallbackId}`,
  borrowerName: a.borrowerName || a.applicantName || "-",
  phone: a.phone || a.contactNumber || "",
  principalAmount: Number(a.principalAmount ?? a.sanctionedAmount) || 0,
  monthlyEmi: Number(a.monthlyEmi ?? a.emiAmount) || 0,
  totalOutstanding: Number(a.totalOutstanding ?? a.outstandingAmount) || 0,
  totalOverdue: Number(a.totalOverdue ?? a.overdueAmount) || 0,
  interestRate: Number(a.interestRate) || 0,
  tenureMonths: Number(a.tenureMonths ?? a.loanTenureMonths ?? a.tenure) || 0,
  dpd: Number(a.dpd) || 0,
  // Overdue count / NPA grade ke naam backend me alag alag hain — sabko tolerate karo
  overdueEmiCount:
    Number(
      a.overdueEmiCount ?? a.overdueEmis ?? a.overdueEmiCnt ?? a.noOfOverdueEmis ?? a.overdueInstallments ?? a.overdueCount
    ) || 0,
  npaClassification: a.npaClassification || a.npaStatus || a.assetClassification || a.npaCategory || "",
  status: a.status || "ACTIVE",
});

export default function LoanAccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const { has } = usePermissions();
  const primaryColor = currentTheme?.color || "#2e856e";
  const canRecordPayment = has("LOAN_PAYMENT_RECORD");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Sirf API data — pehle "Rahul Sharma" ka mock account aur EMI schedule tha jo API fail hone pe dikhta rehta tha
  const [account, setAccount] = useState(null);
  const [emiSchedule, setEmiSchedule] = useState([]);

  // Payment Recording Modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(EMPTY_PAYMENT_FORM);

  // Prepayment Modal
  const [prepayModalOpen, setPrepayModalOpen] = useState(false);
  const [prepayAmount, setPrepayAmount] = useState(0);

  // Auto-Debit (NACH) — mandate bhi sirf API se aata hai, koi dummy mandate nahi
  const [mandate, setMandate] = useState(null);
  const [nachLoading, setNachLoading] = useState(true);
  const [nachError, setNachError] = useState(null);
  const [nachModalOpen, setNachModalOpen] = useState(false);
  const [nachForm, setNachForm] = useState(EMPTY_NACH_FORM);
  const [debitModalOpen, setDebitModalOpen] = useState(false);
  const [debitForm, setDebitForm] = useState(EMPTY_DEBIT_FORM);

  // Downloads (legal notice / statement)
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false); // 💬 EMI reminder / overdue WhatsApp
  const [legalLoading, setLegalLoading] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);

  const fetchAccount = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [accRes, emiRes] = await Promise.allSettled([
        fintechApi.getLoanAccount(id),
        fintechApi.getEmiSchedule(id),
      ]);
      if (accRes.status === "rejected") throw accRes.reason;
      const raw = accRes.value?.data || accRes.value;
      if (!raw) throw new Error("Loan account not found");
      setAccount(normalizeAccount(raw, id));

      if (emiRes.status === "fulfilled") {
        setEmiSchedule(toArray(emiRes.value));
      } else {
        console.error("EMI schedule load failed:", emiRes.reason);
        message.error("Failed to load EMI schedule");
        setEmiSchedule([]);
      }
    } catch (err) {
      console.error("Loan account load failed:", err);
      setLoadError(err.message || "Failed to load loan account");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  // Mandate load — 404 / khaali response ka matlab mandate bana hi nahi hai (NOT_SETUP), error nahi
  const fetchMandate = useCallback(async () => {
    if (!id) return;
    setNachLoading(true);
    setNachError(null);
    try {
      const res = await fintechApi.getNachMandate(id);
      setMandate(normalizeMandate(res));
    } catch (err) {
      setMandate(null);
      if (isNotFoundError(err)) {
        setNachError(null);
      } else {
        console.error("NACH mandate load failed:", err);
        setNachError(err.message || "Failed to load auto-debit mandate");
      }
    } finally {
      setNachLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMandate();
  }, [fetchMandate]);

  const openPaymentModal = () => {
    setPaymentForm({ ...EMPTY_PAYMENT_FORM, amount: account.monthlyEmi });
    setPaymentModalOpen(true);
  };

  // Success pe hi modal band + server se fresh data (local "PAID" marking nahi)
  const handleRecordPayment = async () => {
    if (!(Number(paymentForm.amount) > 0)) {
      message.warning("Please enter a valid payment amount");
      return;
    }
    if (paymentForm.mode !== "CASH" && !paymentForm.transactionId.trim()) {
      message.warning("Please enter the transaction / reference number");
      return;
    }
    setActionLoading(true);
    try {
      await fintechApi.recordPayment(account.id, {
        ...paymentForm,
        transactionId: paymentForm.transactionId.trim() || null,
        remarks: paymentForm.remarks.trim() || null,
      });
      message.success("Payment recorded successfully!");
      setPaymentModalOpen(false);
      fetchAccount();
    } catch (err) {
      console.error("Record payment failed:", err);
      message.error(err.message || "Failed to record payment");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrepayment = async () => {
    if (!(Number(prepayAmount) > 0)) {
      message.warning("Please enter a valid prepayment amount");
      return;
    }
    if (account.totalOutstanding && Number(prepayAmount) > account.totalOutstanding) {
      message.warning(`Prepayment cannot exceed outstanding ${formatINR(account.totalOutstanding)}`);
      return;
    }
    setActionLoading(true);
    try {
      await fintechApi.prepayment(account.id, { amount: prepayAmount });
      message.success(`Part Prepayment of ${formatINR(prepayAmount)} applied!`);
      setPrepayModalOpen(false);
      fetchAccount();
    } catch (err) {
      console.error("Prepayment failed:", err);
      message.error(err.message || "Failed to apply prepayment");
    } finally {
      setActionLoading(false);
    }
  };

  const openNachModal = () => {
    setNachForm({
      ...EMPTY_NACH_FORM,
      accountHolderName: account.borrowerName && account.borrowerName !== "-" ? account.borrowerName : "",
      maxDebitAmount: account.monthlyEmi || null,
    });
    setNachModalOpen(true);
  };

  // Saare fields required — bank ko galat mandate gaya to reject ho jata hai
  const validateNachForm = () => {
    const bankAccountNumber = (nachForm.bankAccountNumber || "").trim();
    const ifscCode = (nachForm.ifscCode || "").trim().toUpperCase();
    const accountHolderName = (nachForm.accountHolderName || "").trim();

    if (!bankAccountNumber) return "Please enter the bank account number";
    if (!/^\d+$/.test(bankAccountNumber)) return "Bank account number must contain digits only";
    if (bankAccountNumber.length < 6 || bankAccountNumber.length > 20)
      return "Bank account number must be between 6 and 20 digits";
    if (!ifscCode) return "Please enter the IFSC code";
    if (ifscCode.length !== 11 || !IFSC_REGEX.test(ifscCode))
      return "IFSC must be 11 characters in the standard format (e.g. HDFC0001234)";
    if (!accountHolderName) return "Please enter the account holder name";
    if (!(Number(nachForm.maxDebitAmount) > 0)) return "Please enter a valid max debit amount";
    if (!nachForm.startDate) return "Please select the mandate start date";
    return null;
  };

  // Success pe hi modal band + section refresh (fail hua to koi success toast nahi)
  const handleRegisterNach = async () => {
    const validationError = validateNachForm();
    if (validationError) {
      message.warning(validationError);
      return;
    }
    setActionLoading(true);
    try {
      const res = await fintechApi.registerNach(account.id, {
        bankAccountNumber: nachForm.bankAccountNumber.trim(),
        ifscCode: nachForm.ifscCode.trim().toUpperCase(),
        accountHolderName: nachForm.accountHolderName.trim(),
        maxDebitAmount: Number(nachForm.maxDebitAmount),
        startDate: nachForm.startDate.format("YYYY-MM-DD"),
      });
      const registeredId = normalizeMandate(res)?.mandateId;
      message.success(
        registeredId
          ? `Auto-debit mandate registered. Mandate ID: ${registeredId}`
          : "Auto-debit mandate registered successfully!"
      );
      setNachModalOpen(false);
      setNachForm(EMPTY_NACH_FORM);
      fetchMandate();
    } catch (err) {
      console.error("NACH registration failed:", err);
      message.error(err.message || "Failed to register auto-debit mandate");
    } finally {
      setActionLoading(false);
    }
  };

  const openDebitModal = () => {
    setDebitForm({ ...EMPTY_DEBIT_FORM, amount: account.monthlyEmi || null });
    setDebitModalOpen(true);
  };

  const handleProcessDebit = async () => {
    if (!(Number(debitForm.amount) > 0)) {
      message.warning("Please enter a valid debit amount");
      return;
    }
    const maxDebit = Number(mandate?.maxDebitAmount) || 0;
    if (maxDebit > 0 && Number(debitForm.amount) > maxDebit) {
      message.warning(`Debit amount cannot exceed the mandate limit of ${formatINR(maxDebit)}`);
      return;
    }
    setActionLoading(true);
    try {
      await fintechApi.processNachDebit(account.id, {
        amount: Number(debitForm.amount),
        mandateId: mandate?.mandateId || null,
        remarks: debitForm.remarks.trim() || null,
      });
      message.success(`Auto-debit of ${formatINR(debitForm.amount)} submitted to the bank.`);
      setDebitModalOpen(false);
      setDebitForm(EMPTY_DEBIT_FORM);
      fetchMandate();
      fetchAccount();
    } catch (err) {
      console.error("NACH debit failed:", err);
      message.error(err.message || "Failed to process auto-debit");
    } finally {
      setActionLoading(false);
    }
  };

  // Legal notice PDF — blob khaali aaya to bhi success mat bolo
  const handleDownloadLegalNotice = async () => {
    if (!account) return;
    setLegalLoading(true);
    try {
      const blob = await fintechApi.downloadLegalNotice(account.id);
      if (!blob || !blob.size) throw new Error("Server returned an empty legal notice file");
      downloadBlob(
        blob,
        `Legal_Notice_${safeFileName(account.accountNumber)}_${dayjs().format("DD-MM-YYYY")}.pdf`
      );
      message.success("Legal notice generated and downloaded!");
      setLegalModalOpen(false);
    } catch (err) {
      console.error("Legal notice generation failed:", err);
      message.error(err.message || "Failed to generate legal notice");
    } finally {
      setLegalLoading(false);
    }
  };

  // Loan statement Excel export
  const handleDownloadStatement = async () => {
    if (!account) return;
    setStatementLoading(true);
    try {
      const blob = await fintechApi.exportLoanStatement(account.id);
      if (!blob || !blob.size) throw new Error("Server returned an empty statement file");
      downloadBlob(
        blob,
        `Loan_Statement_${safeFileName(account.accountNumber)}_${dayjs().format("DD-MM-YYYY")}.xlsx`
      );
      message.success("Loan statement downloaded successfully!");
    } catch (err) {
      console.error("Statement export failed:", err);
      message.error(err.message || "Failed to download loan statement");
    } finally {
      setStatementLoading(false);
    }
  };

  // 🚀 Lazy on-demand Razorpay SDK loader
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const existingScript = document.getElementById("razorpay-sdk");
      if (existingScript) {
        existingScript.onload = () => resolve(true);
        existingScript.onerror = () => resolve(false);
        return;
      }
      const script = document.createElement("script");
      script.id = "razorpay-sdk";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // 💳 Razorpay Online Payment Checkout
  const handleRazorpayCheckout = async (emiRow) => {
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded || !window.Razorpay) {
      message.error("Unable to load Razorpay payment gateway. Please check your network connection.");
      return;
    }

    const hide = message.loading(`Initiating online checkout for EMI #${emiRow.emiNo}...`, 0);
    try {
      // Step 1: Create Order on Backend
      const orderData = await fintechApi.createRazorpayOrder(account.id, {
        emiNo: emiRow.emiNo,
        amount: emiRow.emiAmount,
        dueDate: emiRow.dueDate,
        loanAccountId: account.id,
        borrowerName: account.borrowerName,
        phone: account.phone,
      });
      hide();

      const orderPayload = orderData?.data || orderData;
      const rzpOrderId = orderPayload?.orderId || orderPayload?.id || orderPayload?.razorpayOrderId;
      // Pehle "rzp_test_key" fallback tha — galat key se checkout silently fail hota
      const rzpKeyId = orderPayload?.keyId || orderPayload?.key || process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!rzpOrderId || !rzpKeyId) {
        message.error("Payment gateway is not configured (missing Razorpay order or key). Please contact support.");
        return;
      }
      const rzpAmount = orderPayload?.amount || Math.round(emiRow.emiAmount * 100);

      // Step 2: Open Razorpay Checkout Dialog
      const options = {
        key: rzpKeyId,
        amount: rzpAmount,
        currency: orderPayload?.currency || "INR",
        name: process.env.REACT_APP_APP_NAME || "Loan EMI Payment",
        description: `Loan EMI #${emiRow.emiNo} Payment - ${account.accountNumber}`,
        order_id: rzpOrderId,
        prefill: {
          name: account.borrowerName,
          contact: account.phone,
        },
        theme: {
          color: primaryColor,
        },
        handler: async function (response) {
          const verifyHide = message.loading("Verifying payment signature...", 0);
          try {
            // Step 3: Verify Payment on Backend
            await fintechApi.verifyRazorpayPayment(account.id, {
              orderId: response.razorpay_order_id || rzpOrderId,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              emiNo: emiRow.emiNo,
              amount: emiRow.emiAmount,
            });
            verifyHide();
            message.success(`Payment Successful! Transaction ID: ${response.razorpay_payment_id}`);
            fetchAccount();
          } catch (verErr) {
            verifyHide();
            console.error("Payment verification failed:", verErr);
            message.error(verErr.message || "Payment verification failed. Please contact support.");
          }
        },
        modal: {
          ondismiss: function () {
            message.info("Payment checkout cancelled");
          },
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (err) {
      hide();
      console.error("Razorpay order creation error:", err);
      message.error(err.message || "Failed to initialize payment gateway order");
    }
  };

  if (loading && !account) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: primaryColor, borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="bg-white p-10 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col items-center text-center gap-3">
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <h2 className="text-lg font-bold text-gray-900">Could not load loan account</h2>
        <p className="text-sm text-gray-500 max-w-md">{loadError}</p>
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Go Back
          </button>
          <button
            onClick={fetchAccount}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
            style={{ backgroundColor: primaryColor }}
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const mandateStatus = mandate?.status || "NOT_SETUP";
  const canManageNach = canRecordPayment && account.status !== "CLOSED";
  const canSetupNach = canManageNach && !["ACTIVE", "PENDING"].includes(mandateStatus);

  // Legal notice sirf 3+ overdue EMI ya SMA-2 / NPA classification pe
  const overdueEmiCount = Number(account.overdueEmiCount) || 0;
  const npaClass = String(account.npaClassification || "").toUpperCase().trim();
  const showLegalNotice =
    overdueEmiCount >= 3 || LEGAL_NOTICE_CLASSIFICATIONS.includes(npaClass);

  return (
    <div className="w-full space-y-6 pb-16">
      {/* ── Page Header ── */}
      <div className="bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/fintech/loans")}
            className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl md:text-2xl font-black text-gray-900">
                Loan Account: {account.accountNumber}
              </h1>
              <span
                className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                  ACCOUNT_STATUS_STYLES[account.status] || "bg-gray-100 text-gray-700 border-gray-200"
                }`}
              >
                {account.status}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Borrower: <strong>{account.borrowerName}</strong>
              {account.phone && <> ({account.phone})</>} | Disbursed: {formatDate(account.disbursedOn || account.disbursedAt)}
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Statement export — /fintech/export/loan-statement se Excel blob aata hai */}
          <button
            onClick={handleDownloadStatement}
            disabled={statementLoading}
            title="Download loan statement (Excel)"
            className={`px-4 py-2 bg-gray-50 text-gray-700 rounded-xl border border-gray-200 text-xs font-bold flex items-center gap-1.5 transition-all ${
              statementLoading ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-100"
            }`}
          >
            {statementLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>{statementLoading ? "Preparing..." : "Download Statement"}</span>
          </button>

          {/* 💬 WhatsApp — EMI reminder / overdue message manually bhejo */}
          <button
            onClick={() => setWaOpen(true)}
            title="Send EMI reminder or overdue message on WhatsApp"
            className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl border border-emerald-200 text-xs font-bold flex items-center gap-1.5 transition-all hover:bg-emerald-100"
          >
            <MessageSquare className="w-4 h-4" />
            <span>WhatsApp</span>
          </button>

          {showLegalNotice && (
            <button
              onClick={() => setLegalModalOpen(true)}
              disabled={legalLoading}
              title="Generate legal demand notice for this defaulting account"
              className={`px-4 py-2 bg-red-50 text-red-700 rounded-xl border border-red-200 text-xs font-bold flex items-center gap-1.5 transition-all ${
                legalLoading ? "opacity-60 cursor-not-allowed" : "hover:bg-red-100"
              }`}
            >
              <Gavel className="w-4 h-4" />
              <span>Generate Legal Notice</span>
            </button>
          )}

          {canRecordPayment && account.status !== "CLOSED" && (
            <>
              <button
                onClick={() => {
                  setPrepayAmount(0);
                  setPrepayModalOpen(true);
                }}
                className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl border border-blue-200 text-xs font-bold transition-all flex items-center gap-1.5"
              >
                <TrendingDown className="w-4 h-4" />
                <span>Prepayment</span>
              </button>

              <button
                onClick={openPaymentModal}
                className="px-5 py-2.5 text-white rounded-xl text-xs font-bold shadow-md transition-all hover:brightness-105 active:scale-95 flex items-center gap-1.5"
                style={{ backgroundColor: primaryColor }}
              >
                <CreditCard className="w-4 h-4" />
                <span>Record Offline Payment</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Top Summary 4 Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
          <span className="text-xs font-bold uppercase text-gray-400">Sanctioned Principal</span>
          <div className="text-2xl font-black text-gray-900 mt-1">
            {formatINR(account.principalAmount)}
          </div>
          <p className="text-xs text-gray-500 mt-1">{account.tenureMonths} Months @ {account.interestRate}%</p>
        </div>

        <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
          <span className="text-xs font-bold uppercase text-gray-400">Monthly EMI</span>
          <div className="text-2xl font-black mt-1" style={{ color: primaryColor }}>
            {formatINR(account.monthlyEmi)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Due next on: {formatDate(account.nextDueDate)}</p>
        </div>

        <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
          <span className="text-xs font-bold uppercase text-gray-400">Total Outstanding</span>
          <div className="text-2xl font-black text-blue-700 mt-1">
            {formatINR(account.totalOutstanding)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Remaining balance</p>
        </div>

        <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
          <span className="text-xs font-bold uppercase text-gray-400">Overdue Demand</span>
          <div className={`text-2xl font-black mt-1 ${account.totalOverdue > 0 ? "text-red-700" : "text-gray-900"}`}>
            {formatINR(account.totalOverdue)}
          </div>
          <p className={`text-xs font-bold mt-1 ${account.dpd > 0 ? "text-red-600" : "text-emerald-600"}`}>
            {account.dpd} DPD{account.npaClassification ? ` (${account.npaClassification})` : ""}
          </p>
        </div>
      </div>

      {/* ── Auto-Debit (NACH) Setup ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
              style={{ backgroundColor: primaryColor }}
            >
              <Landmark className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Auto-Debit (NACH) Setup</h2>
              <p className="text-xs text-gray-500">
                Register an e-NACH mandate so EMIs are debited automatically from the borrower's bank account
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <span
              className={`px-2 py-1 rounded-full text-xs font-bold border ${
                NACH_STATUS_STYLES[mandateStatus] || NACH_STATUS_STYLES.NOT_SETUP
              }`}
            >
              {NACH_STATUS_LABELS[mandateStatus] || mandateStatus}
            </span>

            <button
              onClick={fetchMandate}
              disabled={nachLoading}
              className="p-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all"
              title="Refresh mandate status"
            >
              <RefreshCw className={`w-4 h-4 ${nachLoading ? "animate-spin" : ""}`} />
            </button>

            {canManageNach && mandateStatus === "ACTIVE" && (
              <button
                onClick={openDebitModal}
                className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl border border-blue-200 text-xs font-bold transition-all flex items-center gap-1.5"
              >
                <Zap className="w-4 h-4" />
                <span>Process Debit</span>
              </button>
            )}

            {canSetupNach && !nachLoading && !nachError && (
              <button
                onClick={openNachModal}
                className="px-5 py-2.5 bg-[#2e856e] text-white rounded-xl text-xs font-bold shadow-md transition-all hover:brightness-105 active:scale-95 flex items-center gap-1.5"
                style={{ backgroundColor: primaryColor }}
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Setup Auto-Debit</span>
              </button>
            )}
          </div>
        </div>

        {/* Body — loading / error / empty / mandate details */}
        {nachLoading ? (
          <div className="py-8 text-center text-xs font-semibold text-gray-400">
            Loading mandate details...
          </div>
        ) : nachError ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-red-700">Could not load the auto-debit mandate</p>
                <p className="text-xs text-red-600 mt-0.5">{nachError}</p>
              </div>
            </div>
            <button
              onClick={fetchMandate}
              className="px-4 py-2 rounded-xl text-xs font-bold text-white flex items-center gap-1.5 self-start sm:self-auto"
              style={{ backgroundColor: primaryColor }}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        ) : !mandate ? (
          <div className="py-8 text-center">
            <Landmark className="w-9 h-9 mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-bold text-gray-700">No auto-debit mandate registered</p>
            <p className="text-xs text-gray-400 mt-1">
              {canManageNach
                ? "Use \u201cSetup Auto-Debit\u201d to register an e-NACH mandate for this loan account."
                : "You do not have permission to register a mandate for this loan account."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <span className="text-xs font-bold uppercase text-gray-400">Mandate ID / UMRN</span>
              <div className="text-sm font-black text-gray-900 mt-1 font-mono break-all">
                {mandate.mandateId || "-"}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <span className="text-xs font-bold uppercase text-gray-400">Bank Account</span>
              <div className="text-sm font-black text-gray-900 mt-1 font-mono">
                {maskAccountNumber(mandate.bankAccountNumber)}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {mandate.ifscCode || "-"}
                {mandate.accountHolderName ? ` | ${mandate.accountHolderName}` : ""}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <span className="text-xs font-bold uppercase text-gray-400">Max Debit Amount</span>
              <div className="text-sm font-black mt-1" style={{ color: primaryColor }}>
                {mandate.maxDebitAmount ? formatINR(mandate.maxDebitAmount) : "-"}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Start: {formatDate(mandate.startDate)}
                {mandate.lastDebitDate ? ` | Last debit: ${formatDate(mandate.lastDebitDate)}` : ""}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── EMI Amortization Schedule Table ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">
              Loan Amortization & Repayment Ledger
            </h2>
            <p className="text-xs text-gray-500">
              Installment schedule with online Razorpay UPI / Cards checkout option
            </p>
          </div>
          <button
            onClick={fetchAccount}
            disabled={loading}
            className="p-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                <th className="py-3 px-4">EMI #</th>
                <th className="py-3 px-4">Due Date</th>
                <th className="py-3 px-4 text-right">Principal</th>
                <th className="py-3 px-4 text-right">Interest</th>
                <th className="py-3 px-4 text-right">EMI Amount</th>
                <th className="py-3 px-4 text-right">Paid Amount</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4">Paid On</th>
                <th className="py-3 px-4 text-center">Online Payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {emiSchedule.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-gray-400 text-xs font-semibold">
                    No EMI schedule available
                  </td>
                </tr>
              ) : (
                emiSchedule.map((row) => (
                  <tr key={row.emiNo} className="hover:bg-gray-50/50 transition-colors text-xs">
                    <td className="py-3 px-4 font-bold text-gray-900">#{row.emiNo}</td>
                    <td className="py-3 px-4 font-semibold text-gray-700">{formatDate(row.dueDate)}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{formatINR(row.principal)}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{formatINR(row.interest)}</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">{formatINR(row.emiAmount)}</td>
                    <td className="py-3 px-4 text-right font-bold text-emerald-700">{formatINR(row.paidAmount)}</td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
                          EMI_STATUS_STYLES[row.status] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-400">{formatDate(row.paidOn)}</td>
                    <td className="py-3 px-4 text-center">
                      {(row.status === "PENDING" || row.status === "OVERDUE" || row.status === "PARTIAL") ? (
                        <button
                          onClick={() => handleRazorpayCheckout(row)}
                          className="px-3 py-1 text-white rounded-lg text-[11px] font-bold shadow-sm transition-all hover:brightness-95 active:scale-95 flex items-center gap-1.5 mx-auto"
                          style={{ backgroundColor: primaryColor }}
                          title="Pay EMI Online via UPI / Card / NetBanking"
                        >
                          <CreditCard className="w-3 h-3" />
                          <span>Pay Online</span>
                        </button>
                      ) : (
                        <span className="text-[11px] font-semibold text-emerald-600 flex items-center justify-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Cleared</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Record Payment Modal ── */}
      <Modal
        title={<div className="font-bold text-emerald-700">Record EMI Collection / Payment</div>}
        open={paymentModalOpen}
        onOk={handleRecordPayment}
        onCancel={() => setPaymentModalOpen(false)}
        okText="Record Payment"
        confirmLoading={actionLoading}
        okButtonProps={{ style: { backgroundColor: "#10b981", borderColor: "#10b981" } }}
      >
        <div className="space-y-4 py-3 text-sm">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Payment Amount (₹)</label>
            <InputNumber
              className="w-full rounded-xl font-bold"
              min={0}
              value={paymentForm.amount}
              onChange={(val) => setPaymentForm((prev) => ({ ...prev, amount: val }))}
              formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Payment Channel / Mode</label>
            <Select
              className="w-full"
              value={paymentForm.mode}
              onChange={(val) => setPaymentForm((prev) => ({ ...prev, mode: val }))}
              options={[
                { value: "UPI", label: "UPI / QR Code" },
                { value: "NET_BANKING", label: "Net Banking / NEFT / IMPS" },
                { value: "NACH_MANDATE", label: "Auto-Debit NACH Mandate" },
                { value: "CHEQUE", label: "Cheque Deposit" },
                { value: "CASH", label: "Cash Receipt" },
              ]}
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              Transaction ID / Reference Number {paymentForm.mode !== "CASH" && "*"}
            </label>
            <Input
              placeholder={paymentForm.mode === "CHEQUE" ? "Cheque number" : "UTR / UPI reference"}
              value={paymentForm.transactionId}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, transactionId: e.target.value }))}
              className="rounded-xl font-mono uppercase"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Remarks</label>
            <Input
              placeholder="e.g. Monthly EMI clearance"
              value={paymentForm.remarks}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, remarks: e.target.value }))}
              className="rounded-xl"
            />
          </div>
        </div>
      </Modal>

      {/* ── Prepayment Modal ── */}
      <Modal
        title={<div className="font-bold text-blue-700">Apply Part Prepayment / Foreclosure</div>}
        open={prepayModalOpen}
        onOk={handlePrepayment}
        onCancel={() => setPrepayModalOpen(false)}
        okText="Apply Prepayment"
        confirmLoading={actionLoading}
        okButtonProps={{ style: { backgroundColor: "#3b82f6", borderColor: "#3b82f6" } }}
      >
        <div className="space-y-4 py-3 text-sm">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Prepayment Amount (₹)</label>
            <InputNumber
              className="w-full rounded-xl font-bold"
              min={0}
              max={account.totalOutstanding || undefined}
              value={prepayAmount}
              onChange={(val) => setPrepayAmount(val)}
              formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
            />
          </div>
          <p className="text-xs text-gray-500">
            Outstanding: <strong>{formatINR(account.totalOutstanding)}</strong>. Applying this prepayment will reduce the outstanding principal balance directly.
          </p>
        </div>
      </Modal>

      {/* ── NACH Mandate Registration Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 font-bold text-base" style={{ color: primaryColor }}>
            <Landmark className="w-5 h-5" />
            <span>Setup Auto-Debit (e-NACH Mandate)</span>
          </div>
        }
        open={nachModalOpen}
        onOk={handleRegisterNach}
        onCancel={() => setNachModalOpen(false)}
        okText="Register Mandate"
        confirmLoading={actionLoading}
        okButtonProps={{ style: { backgroundColor: primaryColor, borderColor: primaryColor } }}
      >
        <div className="space-y-4 py-3 text-sm">
          <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs">
            <div className="font-bold text-gray-800">{account.borrowerName}</div>
            <div className="text-gray-500">
              A/c: {account.accountNumber} | Monthly EMI: {formatINR(account.monthlyEmi)}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Bank Account Number *</label>
            <Input
              placeholder="Digits only"
              value={nachForm.bankAccountNumber}
              onChange={(e) =>
                setNachForm((prev) => ({
                  ...prev,
                  bankAccountNumber: e.target.value.replace(/\D/g, ""),
                }))
              }
              maxLength={20}
              className="rounded-xl font-mono"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">IFSC Code *</label>
              <Input
                placeholder="e.g. HDFC0001234"
                value={nachForm.ifscCode}
                onChange={(e) =>
                  setNachForm((prev) => ({
                    ...prev,
                    ifscCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                  }))
                }
                maxLength={11}
                className="rounded-xl font-mono uppercase"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Account Holder Name *</label>
              <Input
                placeholder="As per bank records"
                value={nachForm.accountHolderName}
                onChange={(e) => setNachForm((prev) => ({ ...prev, accountHolderName: e.target.value }))}
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Max Debit Amount (₹) *</label>
              <InputNumber
                className="w-full rounded-xl font-bold"
                min={1}
                value={nachForm.maxDebitAmount}
                onChange={(val) => setNachForm((prev) => ({ ...prev, maxDebitAmount: val }))}
                formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
              />
              <p className="text-[11px] text-gray-400 mt-1">Defaults to the monthly EMI amount</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Mandate Start Date *</label>
              <DatePicker
                className="w-full rounded-xl"
                format="DD/MM/YYYY"
                placeholder="Select start date"
                value={nachForm.startDate}
                onChange={(val) => setNachForm((prev) => ({ ...prev, startDate: val }))}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* ── Process NACH Debit Confirm Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-blue-700 font-bold text-base">
            <Zap className="w-5 h-5" />
            <span>Process Auto-Debit</span>
          </div>
        }
        open={debitModalOpen}
        onOk={handleProcessDebit}
        onCancel={() => setDebitModalOpen(false)}
        okText="Confirm & Debit"
        confirmLoading={actionLoading}
        okButtonProps={{ style: { backgroundColor: "#3b82f6", borderColor: "#3b82f6" } }}
      >
        <div className="space-y-4 py-3 text-sm">
          <p className="text-xs text-gray-600">
            Present a debit request to the borrower's bank under mandate{" "}
            <strong className="font-mono">{mandate?.mandateId || "-"}</strong> for{" "}
            <strong>{account.borrowerName}</strong>. This cannot be reversed once presented.
          </p>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Debit Amount (₹)</label>
            <InputNumber
              className="w-full rounded-xl font-bold"
              min={1}
              max={mandate?.maxDebitAmount || undefined}
              value={debitForm.amount}
              onChange={(val) => setDebitForm((prev) => ({ ...prev, amount: val }))}
              formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
            />
            {mandate?.maxDebitAmount ? (
              <p className="text-[11px] text-gray-400 mt-1">
                Mandate limit: {formatINR(mandate.maxDebitAmount)}
              </p>
            ) : null}
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Remarks</label>
            <Input
              placeholder="e.g. Monthly EMI auto-debit"
              value={debitForm.remarks}
              onChange={(e) => setDebitForm((prev) => ({ ...prev, remarks: e.target.value }))}
              className="rounded-xl"
            />
          </div>
        </div>
      </Modal>

      {/* ── Legal Notice Confirm Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-red-600 font-bold text-base">
            <Gavel className="w-5 h-5" />
            <span>Generate Legal Notice</span>
          </div>
        }
        open={legalModalOpen}
        onOk={handleDownloadLegalNotice}
        onCancel={() => setLegalModalOpen(false)}
        okText="Generate & Download"
        confirmLoading={legalLoading}
        okButtonProps={{ danger: true }}
      >
        <div className="space-y-3 py-2 text-sm">
          <p className="text-gray-700 text-xs">
            Generate legal notice for <strong>{account.borrowerName}</strong>? Outstanding:{" "}
            <strong>{formatINR(account.totalOutstanding)}</strong> | Overdue EMIs:{" "}
            <strong>{overdueEmiCount}</strong>
          </p>
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              This is a formal demand notice for a defaulting account
              {npaClass ? ` (classification: ${npaClass})` : ""}. The generated PDF will be downloaded to your device.
            </span>
          </div>
        </div>
      </Modal>

      {/* 💬 EMI reminder / overdue WhatsApp */}
      <LoanWhatsAppModal
        open={waOpen}
        onClose={() => setWaOpen(false)}
        phone={account.phone}
        templateKeys={["EMI_REMINDER", "EMI_OVERDUE"]}
        defaultTemplate={overdueEmiCount > 0 ? "EMI_OVERDUE" : "EMI_REMINDER"}
        context={{
          name: account.borrowerName,
          accountNumber: account.accountNumber,
          emiAmount: account.monthlyEmi,
          dueDate: account.nextDueDate ? formatDate(account.nextDueDate) : "",
        }}
      />
    </div>
  );
}
